import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "@phosphor-icons/react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function ProductCard({ product, priority = false }) {
  const { addItem } = useCart();
  const { user } = useAuth();

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Please login to save favourites"); return; }
    try {
      await api.post(`/wishlist/${product.id}`);
      toast.success("Added to wishlist");
    } catch { toast.error("Failed"); }
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group relative border border-neutral-200 hover:border-black transition-colors duration-300 flex flex-col bg-white no-underline-link"
    >
      <div className="aspect-[4/3] overflow-hidden bg-neutral-100 relative">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading={priority ? "eager" : "lazy"}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {product.new_arrival && <span className="slash-tag">New</span>}
          {product.best_seller && <span className="slash-tag bg-black">Bestseller</span>}
          {product.condition === "used" && <span className="slash-tag bg-neutral-700">Used</span>}
        </div>
        <button
          onClick={handleWishlist}
          data-testid={`wishlist-${product.slug}`}
          aria-label="Add to wishlist"
          className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur border border-neutral-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-[color:var(--vt-orange)] hover:border-[color:var(--vt-orange)]"
        >
          <Heart size={16} weight="regular" />
        </button>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="overline text-neutral-500">{product.brand || product.category}</div>
        <h3 className="font-display font-bold text-base leading-tight">{product.name}</h3>
        <p className="text-xs text-neutral-500 flex-1 line-clamp-2">{product.short_description}</p>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="font-display font-black text-lg">€{product.price?.toFixed(2)}</div>
            <div className="text-[10px] text-neutral-500">{product.stock > 0 ? "In stock" : "Out of stock"}</div>
          </div>
          <button
            onClick={handleAdd}
            data-testid={`add-to-cart-${product.slug}`}
            disabled={product.stock <= 0}
            className="text-[11px] font-bold uppercase tracking-widest px-3 py-2 bg-black text-white hover:bg-[color:var(--vt-orange)] transition-colors disabled:bg-neutral-300 disabled:cursor-not-allowed"
          >
            +Cart
          </button>
        </div>
      </div>
    </Link>
  );
}
